// @ts-nocheck
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SwalToast from '../../components/Common/swalToast';
import {
  loginSuccess,
  setUserPermissions,
} from '../../store/slices/UserSilcer';
import API_ENDPOINTS from '../../const/ApiEndPoints';
import useUser from './useUser';
import useUserGroupProvisioning from '../UserManagement/useUserGroupProvisioning';
import { getLabelPathFromLabelConfig } from '../../utils/Utils';
import { DEFAULT_ROUTE_CONFIG } from '../../const/defaultRoutesConfig';
import { clearStorageWithPreservation } from '../../utils/commonAuthUtils';
import { notifySessionLogout } from '../../utils/sessionBroadcast';

export default function useAuth() {
  const { users } = useSelector((state) => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    createUser,
    suspendUser,
    updateUser,
    getAllUsers,
    getUserSetting,
    updateUserSetting,
  } = useUser();
  const { getAllGroups } = useUserGroupProvisioning();
  const login = (body) => {
    // Get LDAP or SSO login endpoint from localStorage
    // This endpoint comes from the frontend config
    // Login method of tenant depends on their requirement (SSO, SAML, LDAP, etc)
    let loginEndpoint = localStorage.getItem('loginEndpoint');
    const forceNormalAuth = localStorage.getItem('forceNormalAuth');
    if (
      !loginEndpoint ||
      loginEndpoint === 'undefined' ||
      forceNormalAuth === 'true'
    ) {
      loginEndpoint = API_ENDPOINTS.AUTH.LOGIN_USER;
    }

    return axios
      .post(`${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${loginEndpoint}`, body)
      .then((res) => {
        // Setting the organization ID/ realm id/ org so it can be used everywhere
        localStorage.setItem('REALM_ID', res?.data?.profile?.org);

        dispatch(
          loginSuccess({
            token: res.data.tokens.access_token,
            refresh: res.data.tokens.refresh_token,
            user: res.data.user,
            profile: res.data.profile,
            remember: body.remember_me,
          })
        );
        if (window.apm) {
          // www.elastic.co/guide/en/apm/agent/rum-js/current/agent-api.html#apm-set-user-context
          window.apm.setUserContext({
            username: res?.data?.user?.username,
            id: res?.data?.user?.id,
            email: res?.data?.user?.email,
          });
        }
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };
  const logout = (body) => {
    return axios
      .post(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.LOGOUT_USER}`,
        body
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      })
      .finally(() => {
        // Use centralized storage clearing utility
        const preservedValues = clearStorageWithPreservation();
        notifySessionLogout();

        window.open(`/login/${preservedValues.accountId || ''}`, '_self');
      });
  };

  const getAllRoles = (loader = true) => {
    return axios
      .get(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.GET_ALL_ROLES}`,
        { loader }
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  };

  const fetchUserPermissions = () =>
    axios
      .get(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.USER_PERMISSION}`,
        // @ts-ignore
        { loader: false }
      )
      .then((resp) => {
        if (
          window.location.pathname !== '/' &&
          Object.keys(resp?.data?.UserPermission).length <= 0
        ) {
          const accessRequestPath = getLabelPathFromLabelConfig(
            DEFAULT_ROUTE_CONFIG?.accessRequest?.keyPath || ``,
            'path',
            DEFAULT_ROUTE_CONFIG?.accessRequest?.path
          );

          navigate(`/${accessRequestPath}`);
          SwalToast({
            icon: 'info',
            title:
              'This user account is not associated with any group. Please contact admin or Raise access request.',
          });
        }
        dispatch(
          setUserPermissions({
            permissions: resp?.data?.UserPermission,
          })
        );
      })
      .catch((error) => {
        dispatch(
          setUserPermissions({
            permissions: {},
          })
        );
        throw error;
      });
  const forgetPassword = (body) =>
    axios
      .post(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.FORGET_PASSWORD}`,
        body
      )
      .then((res) => {
        return res;
      })
      .catch((err) => {
        throw err;
      });
  const resetPassword = (body) =>
    axios
      .post(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
        body
      )
      .then((res) => {
        return res;
      });
  const getIsLoggedInUserAdminOfAGroup = () => {
    return axios
      .get(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.IS_LOGGED_IN_USER_ADMIN_OF_GROUP}`
      )
      .then((resp) => {
        return resp;
      })
      .catch((error) => {
        throw error;
      });
  };

  const getFrontendConfig = () => {
    return axios
      .get(
        `${API_ENDPOINTS.MODULE_BASE_URL.AUTH}${API_ENDPOINTS.AUTH.FRONTEND_CONFIG}`
      )
      .then((resp) => {
        return resp;
      })
      .catch((error) => {
        throw error;
      });
  };

  return {
    users,
    login,
    getAllUsers,
    getAllRoles,
    createUser,
    fetchUserPermissions,
    updateUser,
    suspendUser,
    forgetPassword,
    resetPassword,
    updateUserSetting,
    getUserSetting,
    getIsLoggedInUserAdminOfAGroup,
    logout,
    getFrontendConfig,
    getAllGroups,
  };
}
